import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { selectUser } from '../redux/slices/userSlice';
import {
  fetchTeams,
  selectTeams,
  setSettingForm,
  teamSetting,
  clearSettingResult,
  setTeamsError,
  teamDelete,
  clearDeleteResult,
  fetchTeamMembers,
  setMemberForm,
  inviteTeamMember,
  clearInviteResult,
  updateTeamMemberRole,
  clearMemberRoleResult,
  deleteTeamMember,
  clearMemberDeleteResult,
  cancelTeamInvitation,
  clearInvitationCancelResult,
} from '../redux/slices/teamsSlice';
import Teams from '../components/Teams';
import styles from './TeamSetting.module.css';
import { useToaster } from '../components/Toaster';
import CheckModal from '../components/CheckModal';

const ROLE_OPTIONS = [
  { value: 1, label: '管理者' },
  { value: 2, label: '編輯者' },
  { value: 3, label: '瀏覽者' },
];

const roleText = (role) => ROLE_OPTIONS.find((item) => item.value === role)?.label || '未知';

const getMemberAvatarText = (member) => {
  const base = `${member?.name || ''}`.trim() || `${member?.mail || ''}`.trim();
  return base ? base.charAt(0).toUpperCase() : '?';
};

const teamStyleSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 2200 }),
  control: (base, state) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#d84040' : '#d0d3d8',
    boxShadow: state.isFocused
      ? 'inset 0 1px 2px rgba(0,0,0,.10), 0 0 0 2px #fff, 0 0 0 4px rgba(255,64,64,.5)'
      : 'inset 0 1px 2px rgba(0,0,0,.08)',
    borderRadius: 8,
    minHeight: 40,
    outline: 'none',
    transition: 'box-shadow .2s ease, border-color .2s ease',
    ':hover': {
      borderColor: state.isFocused ? '#d84040' : 'rgba(0,0,0,.2)',
    },
  }),
  singleValue: (base) => ({ ...base, color: '#101014' }),
  placeholder: (base) => ({ ...base, color: '#868e96' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? '#d84040' : '#495057',
    ':hover': { color: '#d84040' },
  }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'rgba(0,0,0,.12)' }),
  menu: (base) => ({
    ...base,
    overflow: 'hidden',
    marginTop: 6,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    border: '1px solid #d84040',
    boxShadow: '0 18px 36px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.1)',
    zIndex: 2200,
  }),
  menuList: (base) => ({
    ...base,
    padding: 4,
    backgroundColor: '#ffffff',
  }),
  option: (base, state) => ({
    ...base,
    padding: '10px 14px',
    borderRadius: 6,
    transition: 'background .15s ease, color .15s ease, box-shadow .15s ease',
    fontWeight: state.isSelected ? 700 : 500,
    color: state.isDisabled ? '#ccc' : '#101014',
    backgroundColor: state.isSelected || state.isFocused ? 'rgba(216,64,64,.1)' : 'transparent',
    boxShadow: state.isSelected || state.isFocused ? 'inset 4px 0 0 0 #d84040' : 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    ':active': {
      backgroundColor: state.isDisabled ? 'transparent' : 'rgba(216,64,64,.14)',
    },
  }),
};

export default function TeamSetting() {
  const user = useSelector(selectUser);
  const teams = useSelector(selectTeams);
  const dispatch = useDispatch();
  const { push } = useToaster();
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const [memberPendingRemove, setMemberPendingRemove] = useState(null);

  const selectedTeam = useMemo(
    () => teams.teams.find((item) => item.token === teams.team_selected) || null,
    [teams.teams, teams.team_selected]
  );
  const isManager = selectedTeam?.account_role === 1;
  const canEditTeam = [1, 2].includes(selectedTeam?.account_role);
  const teamDeleteDisabled = !isManager;

  const handleInputChange = (e) => {
    dispatch(
      setSettingForm({
        ...teams.setting_form,
        [e.target.name]: e.target.value,
      })
    );
  };

  const handleMemberFormChange = (e) => {
    const value = e.target.name === 'account_role' ? Number(e.target.value) : e.target.value;
    dispatch(setMemberForm({ [e.target.name]: value }));
  };

  const handleSubmit = () => {
    if (!teams?.setting_form?.name?.trim()) {
      dispatch(setTeamsError({ msg: '請輸入團隊名稱！' }));
      return;
    }

    dispatch(
      teamSetting({
        account_token: user?.data?.token,
        team_token: teams?.team_selected,
        name: teams?.setting_form?.name?.trim(),
      })
    );
  };

  const handleInviteSubmit = () => {
    if (!isManager) return;

    const mail = (teams?.member_form?.mail || '').trim().toLowerCase();
    if (!mail) {
      dispatch(setTeamsError({ msg: '請輸入對方的 Google 帳號 Email！' }));
      return;
    }

    dispatch(
      inviteTeamMember({
        account_token: user?.data?.token,
        team_token: teams?.team_selected,
        mail,
        account_role: Number(teams?.member_form?.account_role || 2),
      })
    );
  };

  const handleDeleteClick = () => {
    setCheckModalOpen(true);
  };

  const handleDelete = () => {
    setCheckModalOpen(false);

    dispatch(
      teamDelete({
        team_token: teams?.team_selected,
        account_token: user?.data?.token,
      })
    );
  };

  const handleRoleChange = (member, nextRole) => {
    if (!isManager || !teams?.team_selected) return;
    const parsedRole = Number(nextRole);
    if (parsedRole === Number(member.account_role)) return;

    dispatch(
      updateTeamMemberRole({
        account_token: user?.data?.token,
        team_token: teams?.team_selected,
        target_account_token: member.account_token,
        account_role: parsedRole,
      })
    );
  };

  const handleRemoveMember = (member) => {
    if (!isManager || !teams?.team_selected) return;

    setMemberPendingRemove(member);
    setRemoveMemberModalOpen(true);
  };

  const handleRemoveMemberConfirm = () => {
    if (!memberPendingRemove) return;

    setRemoveMemberModalOpen(false);

    dispatch(
      deleteTeamMember({
        account_token: user?.data?.token,
        team_token: teams?.team_selected,
        target_account_token: memberPendingRemove.account_token,
      })
    );

    setMemberPendingRemove(null);
  };

  const handleRemoveMemberCancel = () => {
    setRemoveMemberModalOpen(false);
    setMemberPendingRemove(null);
  };

  const handleCancelInvite = (invite) => {
    if (!isManager || !teams?.team_selected) return;

    const ok = window.confirm(`確定要取消寄給 ${invite.invite_mail} 的邀請嗎？`);
    if (!ok) return;

    dispatch(
      cancelTeamInvitation({
        account_token: user?.data?.token,
        team_token: teams?.team_selected,
        invitation_token: invite.token,
      })
    );
  };

  const reloadTeamMembers = (teamToken) => {
    if (!user?.data?.token || !teamToken) return;

    dispatch(
      fetchTeamMembers({
        account_token: user.data.token,
        team_token: teamToken,
      })
    );
  };

  useEffect(() => {
    if (user?.data?.token && teams?.team_selected) {
      dispatch(
        fetchTeamMembers({
          account_token: user?.data?.token,
          team_token: teams?.team_selected,
        })
      );
    }
  }, [dispatch, user?.data?.token, teams?.team_selected]);

  useEffect(() => {
    if (teams?.setting_result?.token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));
      push({ title: '修改成功', message: '團隊設定已更新', variant: 'success' });
      dispatch(clearSettingResult());
    }
  }, [teams.setting_result, push, dispatch, user?.data?.token]);

  useEffect(() => {
    if (teams?.delete_result?.token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));
      push({ title: '刪除成功', message: '團隊已刪除', variant: 'success' });
      dispatch(clearDeleteResult());
    }
  }, [teams.delete_result, push, dispatch, user?.data?.token]);

  useEffect(() => {
    if (teams?.invite_result?.team_token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));
      reloadTeamMembers(teams.invite_result.team_token);
      dispatch(setMemberForm({ mail: '', account_role: teams.member_form.account_role || 2 }));
      push({
        title: '邀請已處理',
        message: teams.invite_result.mail_sent === false
          ? '成員資料已建立，但 Gmail API 信件尚未寄出，請檢查寄信設定。'
          : '邀請已送出或成員已直接加入團隊。',
        variant: teams.invite_result.mail_sent === false ? 'warning' : 'success',
      });
      dispatch(clearInviteResult());
    }
  }, [teams.invite_result, push, dispatch, user?.data?.token, teams.member_form.account_role]);

  useEffect(() => {
    if (teams?.member_role_result?.team_token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));
      reloadTeamMembers(teams.member_role_result.team_token);
      push({ title: '修改成功', message: '成員權限已更新', variant: 'success' });
      dispatch(clearMemberRoleResult());
    }
  }, [teams.member_role_result, push, dispatch, user?.data?.token]);

  useEffect(() => {
    if (teams?.member_delete_result?.team_token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));

      if (teams.member_delete_result.target_account_token !== user?.data?.token) {
        reloadTeamMembers(teams.member_delete_result.team_token);
      }

      push({ title: '移除成功', message: '成員已移出團隊', variant: 'success' });
      dispatch(clearMemberDeleteResult());
    }
  }, [teams.member_delete_result, push, dispatch, user?.data?.token]);

  useEffect(() => {
    if (teams?.invitation_cancel_result?.team_token) {
      dispatch(fetchTeams({ account_token: user?.data?.token }));
      reloadTeamMembers(teams.invitation_cancel_result.team_token);
      push({ title: '取消成功', message: '邀請已取消', variant: 'success' });
      dispatch(clearInvitationCancelResult());
    }
  }, [teams.invitation_cancel_result, push, dispatch, user?.data?.token]);

  return (
    <>
      <section className={styles.teamsWrap}>
        <div className={`${styles.panel} mt-2`}>
          <div className={`px-2 px-md-5 py-2 py-md-5 ${styles.content} row g-4`}>
            <div className="col-12">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-diagram-3 me-2" />
                  團隊選擇
                </h4>
                <div className="mt-2">
                  <Teams />
                </div>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-people me-2" />
                  團隊成員管理
                </h4>

                <div className={styles.summaryRow}>
                  <span className={styles.summaryBadge}>目前成員 {teams.members.length} 人</span>
                  <span className={styles.summaryBadge}>待接受邀請 {teams.pending_invites.length} 筆</span>
                  <span className={styles.summaryHint}>
                    {isManager ? '你目前是管理者，可新增、改權限、移除成員。' : '只有管理者可以異動團隊成員。'}
                  </span>
                </div>

                {isManager && (
                  <div className={styles.inviteCard}>
                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-lg-6">
                        <label className={`${styles.fieldLabel} form-label mb-2`}>
                          <span className={styles.labelDot} aria-hidden="true" />
                          Google 帳號 Email
                        </label>
                        <input
                          type="email"
                          className={`form-control ${styles.input}`}
                          placeholder="輸入對方的 Google Email"
                          name="mail"
                          value={teams?.member_form?.mail ?? ''}
                          onChange={handleMemberFormChange}
                        />
                      </div>

                      <div className="col-12 col-lg-3">
                        <label className={`${styles.fieldLabel} form-label mb-2`}>
                          <span className={styles.labelDot} aria-hidden="true" />
                          權限
                        </label>
                        <Select
                          inputId="team-member-invite-role"
                          instanceId="team-member-invite-role"
                          className={styles.memberRoleSelect}
                          options={ROLE_OPTIONS}
                          value={ROLE_OPTIONS.find((item) => item.value === Number(teams?.member_form?.account_role ?? 2)) || ROLE_OPTIONS[1]}
                          onChange={(opt) => handleMemberFormChange({
                            target: {
                              name: 'account_role',
                              value: opt?.value ?? 2,
                            },
                          })}
                          placeholder="選擇權限…"
                          isSearchable={false}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPosition="fixed"
                          styles={teamStyleSelectStyles}
                        />
                      </div>

                      <div className="col-12 col-lg-3">
                        <button
                          type="button"
                          className={`${styles.btnSave} w-100`}
                          onClick={handleInviteSubmit}
                        >
                          <i className="bi bi-person-plus me-1" />
                          新增 / 邀請成員
                        </button>
                      </div>
                    </div>
                    <div className={styles.inviteHelp}>
                      若對方尚未登入過系統，會先建立待接受邀請；對方之後用同一個 Google 帳號登入，就會自動加入團隊。
                    </div>
                  </div>
                )}

                <div className={styles.memberListWrap}>
                  {teams.members.length === 0 ? (
                    <div className={styles.emptyBox}>目前沒有成員資料。</div>
                  ) : (
                    teams.members.map((member) => {
                      const isSelf = member.account_token === user?.data?.token;
                      return (
                        <div key={member.account_token} className={styles.memberRow}>
                          <div className={styles.memberIdentity}>
                            <div className={styles.memberAvatar}>
                              {member.image ? (
                                <>
                                  <img
                                    src={member.image}
                                    alt={member.name || member.mail || 'member avatar'}
                                    className={styles.memberAvatarImg}
                                    referrerPolicy="no-referrer"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.parentElement?.querySelector('[data-avatar-fallback="true"]');
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <span
                                    data-avatar-fallback="true"
                                    className={styles.memberAvatarFallback}
                                    style={{ display: 'none' }}
                                  >
                                    {getMemberAvatarText(member)}
                                  </span>
                                </>
                              ) : (
                                <span className={styles.memberAvatarFallback}>
                                  {getMemberAvatarText(member)}
                                </span>
                              )}
                            </div>

                            <div className={styles.memberMain}>
                              <div className={styles.memberNameRow}>
                                <span className={styles.memberName}>{member.name || '未命名使用者'}</span>
                                {isSelf && <span className={styles.selfBadge}>你</span>}
                              </div>
                              <div className={styles.memberMail}>{member.mail}</div>
                            </div>
                          </div>

                          <div className={styles.memberActions}>
                            <span className={styles.roleChip}>{roleText(member.account_role)}</span>

                            {isManager ? (
                              <Select
                                inputId={`team-member-role-${member.account_token}`}
                                instanceId={`team-member-role-${member.account_token}`}
                                className={styles.memberSelect}
                                options={ROLE_OPTIONS}
                                value={ROLE_OPTIONS.find((item) => item.value === Number(member.account_role)) || null}
                                onChange={(opt) => handleRoleChange(member, opt?.value)}
                                placeholder="選擇權限…"
                                isSearchable={false}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                                styles={teamStyleSelectStyles}
                              />
                            ) : null}

                            {isManager ? (
                              <button
                                type="button"
                                className={styles.btnGhostDanger}
                                onClick={() => handleRemoveMember(member)}
                              >
                                <i className="bi bi-person-dash me-1" />
                                移除
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-4">
                  <h5 className={styles.subTitle}>待接受邀請</h5>
                  <div className={styles.memberListWrap}>
                    {teams.pending_invites.length === 0 ? (
                      <div className={styles.emptyBox}>目前沒有待接受邀請。</div>
                    ) : (
                      teams.pending_invites.map((invite) => (
                        <div key={invite.token} className={styles.memberRow}>
                          <div className={styles.memberMain}>
                            <div className={styles.memberNameRow}>
                              <span className={styles.memberName}>{invite.invite_mail}</span>
                              <span className={styles.pendingBadge}>尚未登入</span>
                            </div>
                            <div className={styles.memberMail}>
                              權限：{roleText(invite.account_role)}
                              {invite.mail_send_time ? ' ・ 已寄送通知' : ' ・ 尚未成功寄信'}
                            </div>
                          </div>

                          <div className={styles.memberActions}>
                            <span className={styles.roleChip}>{roleText(invite.account_role)}</span>
                            {isManager ? (
                              <button
                                type="button"
                                className={styles.btnGhostDanger}
                                onClick={() => handleCancelInvite(invite)}
                              >
                                <i className="bi bi-x-circle me-1" />
                                取消邀請
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-gear me-2" />
                  一般設定
                </h4>

                <form
                  className={styles.form}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="mb-3">
                    <label htmlFor="team-setting_teamName" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      團隊名稱 <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      className={`form-control ${styles.input}`}
                      id="team-setting_teamName"
                      placeholder="輸入團隊名稱"
                      name="name"
                      value={teams?.setting_form?.name ?? ''}
                      onChange={handleInputChange}
                      disabled={!canEditTeam}
                      maxLength={20}
                    />
                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 20 字。請使用容易辨識的名稱。</small>
                      <small className={styles.charCount}>{(teams?.setting_form?.name ?? '').length}/20</small>
                    </div>
                  </div>

                  <div className={styles.actions}>
                    <button type="button" className={styles.btnSave} disabled={!canEditTeam} onClick={handleSubmit}>
                      <i className="bi bi-floppy me-1" />
                      保存修改
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`${styles.block} ${styles.dangerBlock}`}>
                <h4 className={`${styles.sectionTitle} ${styles.dangerTitle}`}>
                  <i className="bi bi-exclamation-octagon-fill me-2" />
                  刪除團隊
                </h4>

                <div className={`mt-3 ${styles.dangerBody}`}>
                  <p className="mt-2 mb-1">一旦你刪除了團隊帳號，將無法恢復。請務必確認。</p>
                  <p className="mb-1">所有已上傳的資料與訓練過的代理人都將被刪除。</p>
                  <p className="mb-0">此操作無法復原。</p>
                </div>

                <div className="text-end mt-3">
                  <button className={styles.btnDanger} disabled={teamDeleteDisabled} onClick={handleDeleteClick}>
                    <i className="bi bi-trash3 me-1" />
                    刪除{selectedTeam ? ` ${selectedTeam.name}` : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CheckModal
        modalOpen={checkModalOpen}
        title={'請確認'}
        msg={`您是否要刪除「${selectedTeam?.name || ''}」？`}
        afterYes={handleDelete}
        afterNo={() => setCheckModalOpen(false)}
      />

      <CheckModal
        modalOpen={removeMemberModalOpen}
        title={'請確認'}
        msg={`您是否要把「${memberPendingRemove?.name || memberPendingRemove?.mail || ''}」移出團隊？`}
        afterYes={handleRemoveMemberConfirm}
        afterNo={handleRemoveMemberCancel}
      />
    </>
  );
}
